import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { TrendUp, UsersThree, Buildings, Detective, Newspaper, ArrowUpRight } from "@phosphor-icons/react";

function Col({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2"><Icon size={20} weight="duotone" className="text-secondary" /><h2 className="font-heading text-lg font-medium">{title}</h2></div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export default function Trending() {
  useSeo("Trending Today", "What's happening across AlumniPulse right now.");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/trending").then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  return (
    <div className="mx-auto max-w-7xl px-5 py-14">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary"><TrendUp size={24} weight="duotone" /></span>
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Trending today</h1>
          <p className="text-sm text-muted-foreground">A live pulse of institutions, communities, reconnections and news.</p>
        </div>
      </div>

      {!d ? <p className="mt-10 text-muted-foreground">Loading…</p> : (
        <div className="mt-8 grid gap-5 lg:grid-cols-2 xl:grid-cols-4">
          <Col icon={Buildings} title="Popular institutions">
            {d.institutions.map((i) => (
              <Link key={i.id} to={`/institutions/${i.id}`} className="flex items-center justify-between gap-3 rounded-lg p-2 hover:bg-accent/50">
                <span className="min-w-0"><span className="block truncate text-sm font-medium">{i.name}</span><span className="text-xs text-muted-foreground">{i.country || i.type}</span></span>
                <span className="shrink-0 text-xs text-secondary">{i.alumni_count} alumni</span>
              </Link>
            ))}
            {d.institutions.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet.</p>}
          </Col>
          <Col icon={UsersThree} title="Active communities">
            {d.groups.map((g) => (
              <Link key={g.id} to={`/groups/${g.id}`} className="flex items-center justify-between gap-3 rounded-lg p-2 hover:bg-accent/50">
                <span className="min-w-0 truncate text-sm font-medium">{g.name}</span>
                <span className="shrink-0 text-xs text-secondary">{g.member_count}</span>
              </Link>
            ))}
            {d.groups.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet.</p>}
          </Col>
          <Col icon={Detective} title="Recent reconnections">
            {d.find_requests.map((f) => (
              <Link key={f.id} to={`/find/r/${f.public_slug}`} className="block rounded-lg p-2 hover:bg-accent/50">
                <span className="block truncate text-sm font-medium">Looking for {f.subject_name}</span>
                {f.institution && <span className="text-xs text-muted-foreground">{f.institution}</span>}
              </Link>
            ))}
            {d.find_requests.length === 0 && <p className="text-sm text-muted-foreground">No open searches.</p>}
          </Col>
          <Col icon={Newspaper} title="Latest news">
            {d.news.map((n) => (
              <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="flex items-start justify-between gap-2 rounded-lg p-2 hover:bg-accent/50">
                <span className="min-w-0"><span className="block truncate text-sm font-medium">{n.title}</span><span className="text-xs text-muted-foreground">{n.source}</span></span>
                <ArrowUpRight size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
              </a>
            ))}
            {d.news.length === 0 && <p className="text-sm text-muted-foreground">No news yet.</p>}
          </Col>
        </div>
      )}
    </div>
  );
}
